import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import HDExportButton from './HDExportButton';

interface Release {
  id: string | number;
  date: string;
  title: string;
  hasLocalChanges?: boolean;
  tileTemplate?: string;
}

interface CompareSwipeProps {
  releaseA: string | number | null;
  releaseB: string | number | null;
  selectedLocation: any;
  releases: Release[];
  onReleaseAChange: (id: string | number | null) => void;
  onReleaseBChange: (id: string | number | null) => void;
  onChangeDetectionComplete?: (job: any) => void;
  aoi?: any;
}

const CompareSwipe: React.FC<CompareSwipeProps> = ({
  releaseA,
  releaseB,
  selectedLocation,
  releases,
  onReleaseAChange,
  onReleaseBChange,
  onChangeDetectionComplete,
  aoi,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapARef = useRef<maplibregl.Map | null>(null);
  const mapBRef = useRef<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [opacity, setOpacity] = useState(0.5);
  const [controlsCollapsed, setControlsCollapsed] = useState(false);

  useEffect(() => {
    if (containerRef.current && !mapARef.current && !mapBRef.current) {
      // Create container for side-by-side maps
      const container = containerRef.current;
      container.innerHTML = '';

      // Create left map container
      const leftContainer = document.createElement('div');
      leftContainer.id = 'map-left';
      leftContainer.style.width = '50%';
      leftContainer.style.height = '100%';
      leftContainer.style.position = 'absolute';
      leftContainer.style.left = '0';
      leftContainer.style.top = '0';
      container.appendChild(leftContainer);

      // Create right map container
      const rightContainer = document.createElement('div');
      rightContainer.id = 'map-right';
      rightContainer.style.width = '50%';
      rightContainer.style.height = '100%';
      rightContainer.style.position = 'absolute';
      rightContainer.style.right = '0';
      rightContainer.style.top = '0';
      container.appendChild(rightContainer);

      // Create divider
      const divider = document.createElement('div');
      divider.id = 'map-divider';
      divider.style.width = '4px';
      divider.style.height = '100%';
      divider.style.position = 'absolute';
      divider.style.left = '50%';
      divider.style.top = '0';
      divider.style.transform = 'translateX(-50%)';
      divider.style.backgroundColor = '#fff';
      divider.style.cursor = 'ew-resize';
      divider.style.zIndex = '1000';
      container.appendChild(divider);

      // Get initial center from selectedLocation or default to a neutral location
      const initialCenter: [number, number] = selectedLocation?.coordinates 
        ? [selectedLocation.coordinates[0], selectedLocation.coordinates[1]]
        : [0, 0]; // Default to center of world if no location selected
      const initialZoom = selectedLocation ? 12 : 2;

      // Initialize left map (Map A)
      // preserveDrawingBuffer: true is required for WebGL canvas capture (HD export)
      mapARef.current = new maplibregl.Map({
        container: 'map-left',
        preserveDrawingBuffer: true,
        style: {
          version: 8,
          sources: {
            'esri-world-imagery': {
              type: 'raster',
              tiles: [
                'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256
            }
          },
          layers: [
            {
              id: 'esri-world-imagery',
              type: 'raster',
              source: 'esri-world-imagery',
              paint: { 'raster-opacity': 1 }
            }
          ]
        },
        center: initialCenter,
        zoom: initialZoom
      });

      // Initialize right map (Map B)
      mapBRef.current = new maplibregl.Map({
        container: 'map-right',
        preserveDrawingBuffer: true,
        style: {
          version: 8,
          sources: {
            'esri-world-imagery': {
              type: 'raster',
              tiles: [
                'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256
            }
          },
          layers: [
            {
              id: 'esri-world-imagery',
              type: 'raster',
              source: 'esri-world-imagery',
              paint: { 'raster-opacity': 1 }
            }
          ]
        },
        center: initialCenter,
        zoom: initialZoom
      });

      // Synchronize map movements
      const syncMaps = (sourceMap: maplibregl.Map, targetMap: maplibregl.Map) => {
        sourceMap.on('move', () => {
          if (targetMap.isMoving()) return;
          targetMap.setCenter(sourceMap.getCenter());
          targetMap.setZoom(sourceMap.getZoom());
        });
      };

      syncMaps(mapARef.current, mapBRef.current);
      syncMaps(mapBRef.current, mapARef.current);

      // Add navigation controls
      mapARef.current.addControl(new maplibregl.NavigationControl(), 'top-left');
      mapBRef.current.addControl(new maplibregl.NavigationControl(), 'top-left');

      // Wait for both maps to load
      Promise.all([
        new Promise(resolve => mapARef.current!.on('load', resolve)),
        new Promise(resolve => mapBRef.current!.on('load', resolve))
      ]).then(() => {
        setMapLoaded(true);
      });

      // Implement swipe functionality
      let isDragging = false;
      let animationFrameId: number | null = null;
      
      const handleMouseDown = (_e: MouseEvent) => {
        isDragging = true;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        // Prevent text selection during drag
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'ew-resize';
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        
        // Cancel any pending animation frame
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
        
        // Use requestAnimationFrame to throttle updates for smooth performance
        animationFrameId = requestAnimationFrame(() => {
          const rect = container.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
          
          // Update container widths
          leftContainer.style.width = `${percentage}%`;
          rightContainer.style.width = `${100 - percentage}%`;
          divider.style.left = `${percentage}%`;
          
          // Immediately resize both maps to prevent blanking
          // This is critical - maps need to know their container size changed
          if (mapARef.current && mapARef.current.loaded()) {
            mapARef.current.resize();
          }
          if (mapBRef.current && mapBRef.current.loaded()) {
            mapBRef.current.resize();
          }
        });
      };

      const handleMouseUp = () => {
        isDragging = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        
        // Cancel any pending animation frame
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
        
        // Final resize to ensure maps are properly rendered
        if (mapARef.current && mapARef.current.loaded()) {
          mapARef.current.resize();
        }
        if (mapBRef.current && mapBRef.current.loaded()) {
          mapBRef.current.resize();
        }
      };

      divider.addEventListener('mousedown', handleMouseDown);
      
      // Cleanup on unmount
      return () => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }

    return () => {
      if (mapARef.current) {
        mapARef.current.remove();
        mapARef.current = null;
      }
      if (mapBRef.current) {
        mapBRef.current.remove();
        mapBRef.current = null;
      }
    };
  }, []);

  // Update release layers
  useEffect(() => {
    if (mapLoaded && mapARef.current && mapBRef.current) {
      setWaybackRelease(mapARef.current, releaseA, 'A');
      setWaybackRelease(mapBRef.current, releaseB, 'B');
    }
  }, [releaseA, releaseB, mapLoaded]);

  // Update map centers when selectedLocation changes
  useEffect(() => {
    if (mapLoaded && selectedLocation?.coordinates && mapARef.current && mapBRef.current) {
      const [lng, lat] = selectedLocation.coordinates;
      mapARef.current.flyTo({
        center: [lng, lat],
        zoom: 12,
        duration: 1000
      });
      // Map B will sync automatically via the syncMaps function
    }
  }, [selectedLocation, mapLoaded]);

  const setWaybackRelease = (mapInstance: maplibregl.Map, releaseId: string | number | null, mapId: string = '') => {
    const srcId = `wayback-release-src-${mapId}`;
    const layerId = `wayback-release-lyr-${mapId}`;

    if (releaseId == null) {
      if (mapInstance.getLayer(layerId)) {
        mapInstance.setLayoutProperty(layerId, 'visibility', 'none');
      }
      return;
    }

    // Look up the release to get its tileTemplate
    const release = releases.find(r => String(r.id) === String(releaseId));
    let tiles: string[];

    if (release?.tileTemplate) {
      // Use the release's own tile template (works for EOSDA, Copernicus, and Esri)
      tiles = [release.tileTemplate];
    } else if (typeof releaseId === 'string' && releaseId.startsWith('copernicus_')) {
      // Copernicus releases without tileTemplate — skip (can't render without proper endpoint)
      console.warn('Copernicus release without tileTemplate, skipping:', releaseId);
      return;
    } else if (typeof releaseId === 'string' && releaseId.startsWith('eosda_')) {
      // EOSDA fallback
      tiles = [`/api/tiles/eosda/${releaseId}/{z}/{x}/{y}`];
    } else {
      // Traditional Esri Wayback release
      tiles = [`/api/tiles/wayback/${releaseId}/{z}/{x}/{y}.jpg`];
    }

    // Remove existing source and layer if they exist
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
    if (mapInstance.getSource(srcId)) {
      mapInstance.removeSource(srcId);
    }

    // Add new source and layer
    mapInstance.addSource(srcId, {
      type: 'raster',
      tiles,
      tileSize: 256
    });
    mapInstance.addLayer({
      id: layerId,
      type: 'raster',
      source: srcId,
      paint: { 'raster-opacity': 1 }
    });
  };

  const [analyzing, setAnalyzing] = useState(false);

  const analyzeChanges = async () => {
    if (!mapARef.current || !mapBRef.current || !releaseA || !releaseB) return;

    try {
      setAnalyzing(true);

      // Get current map bounds for the AOI
      const bounds = mapARef.current.getBounds();
      const nw = bounds.getNorthWest();
      const se = bounds.getSouthEast();

      // Build AOI from current map view bounds or use passed-in AOI
      const analysisAoi = aoi?.geometry || {
        type: "Polygon",
        coordinates: [[
          [nw.lng, nw.lat],
          [se.lng, nw.lat],
          [se.lng, se.lat],
          [nw.lng, se.lat],
          [nw.lng, nw.lat],
        ]],
      };

      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

      // Call the real change detection API (same as EnhancedChangeDetection)
      const response = await fetch('/api/geoint/wayback/change-detection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          aoi: analysisAoi,
          before_release_id: String(releaseA),
          after_release_id: String(releaseB),
          method: 'spectral_diff',
          threshold: 0.15,
          sensitivity: 'medium',
        }),
      });

      if (!response.ok) {
        throw new Error(`Change detection API failed: ${response.statusText}`);
      }

      const result = await response.json();
      const jobId = result.job_id;

      if (!jobId) {
        throw new Error('No job_id returned from change detection API');
      }

      // Poll for job completion
      let job: any = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const statusResp = await fetch(`/api/geoint/wayback/change-detection/${jobId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (!statusResp.ok) continue;
        job = await statusResp.json();
        if (job.status === 'completed' || job.status === 'failed') break;
      }

      if (job?.status === 'completed') {
        // Add heatmap overlay on both maps using the preview image
        const previewUrl = `/api/geoint/wayback/change-detection/${jobId}/preview.jpg`;
        addHeatmapOverlay(mapARef.current, 'A', previewUrl, bounds);
        addHeatmapOverlay(mapBRef.current, 'B', previewUrl, bounds);

        // Propagate results to parent for report generation
        if (onChangeDetectionComplete) {
          onChangeDetectionComplete(job);
        }

        const metrics = job.metrics || {};
        alert(
          `Change detection complete!\n` +
          `Changed area: ${(metrics.changed_area || 0).toFixed(3)} km²\n` +
          `Change: ${(metrics.change_percentage || 0).toFixed(2)}%\n` +
          `Tiles analyzed: ${metrics.tiles_analyzed || 0}`
        );
      } else {
        alert(`Change detection ${job?.status === 'failed' ? 'failed' : 'timed out'}. Please try again.`);
      }
    } catch (error) {
      console.error('Change detection analysis failed:', error);
      alert('Change detection analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const addHeatmapOverlay = (
    mapInstance: maplibregl.Map,
    mapId: string,
    imageUrl: string,
    bounds: maplibregl.LngLatBounds,
  ) => {
    const srcId = `heatmap-overlay-src-${mapId}`;
    const layerId = `heatmap-overlay-${mapId}`;

    if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
    if (mapInstance.getSource(srcId)) mapInstance.removeSource(srcId);

    const nw = bounds.getNorthWest();
    const ne = bounds.getNorthEast();
    const se = bounds.getSouthEast();
    const sw = bounds.getSouthWest();

    mapInstance.addSource(srcId, {
      type: 'image',
      url: imageUrl,
      coordinates: [
        [nw.lng, nw.lat],
        [ne.lng, ne.lat],
        [se.lng, se.lat],
        [sw.lng, sw.lat],
      ],
    });

    mapInstance.addLayer({
      id: layerId,
      type: 'raster',
      source: srcId,
      paint: { 'raster-opacity': 0.6 },
    });
  };

  return (
    <div className="relative w-full h-full bg-background" style={{ zIndex: 1 }}>
      <div ref={containerRef} className="w-full h-full" style={{ zIndex: 1 }} />
      
      {/* Controls - Fixed positioning to not overlap maps - moved to right side, collapsible */}
      <div className={`absolute top-4 right-4 bg-black/95 border border-green-500/30 rounded-lg shadow-xl z-[1000] backdrop-blur-sm transition-all duration-300 ${controlsCollapsed ? 'w-12 p-2' : 'w-72 p-3'}`}>
        <div className="flex items-center justify-between mb-2">
          {!controlsCollapsed && <h3 className="text-lg font-semibold text-green-400">Compare Mode</h3>}
          <button
            onClick={() => setControlsCollapsed(!controlsCollapsed)}
            className="text-green-400 hover:text-green-300 transition-colors ml-auto"
            title={controlsCollapsed ? 'Expand controls' : 'Collapse controls'}
          >
            {controlsCollapsed ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>
        {!controlsCollapsed && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-green-300 mb-1">Map A (Left)</label>
                  <select
                    value={releaseA || ''}
                    onChange={(e) => onReleaseAChange(e.target.value || null)}
                    className="w-full p-2 border border-green-500/50 rounded-md text-sm bg-black/80 text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="" className="bg-black text-white">Select release...</option>
                    {releases.map((release) => (
                      <option key={release.id} value={release.id} className="bg-black text-white">
                        {release.date} - {release.title}
                      </option>
                    ))}
                  </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-green-300 mb-1">Map B (Right)</label>
            <select
              value={releaseB || ''}
              onChange={(e) => onReleaseBChange(e.target.value || null)}
              className="w-full p-2 border border-green-500/50 rounded-md text-sm bg-black/80 text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="" className="bg-black text-white">Select release...</option>
              {releases.map((release) => (
                <option key={release.id} value={release.id} className="bg-black text-white">
                  {release.date} - {release.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Opacity: {Math.round(opacity * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
          </div>
          <div className="pt-2">
            <button
              onClick={() => {
                if (!releaseA || !releaseB) {
                  alert('Please select both releases for comparison');
                  return;
                }
                analyzeChanges();
              }}
              disabled={analyzing}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {analyzing ? 'Analyzing...' : 'Analyze Changes'}
            </button>
          </div>
          
          {/* HD Export Button */}
          <div className="pt-2">
            <HDExportButton
              mapARef={mapARef}
              mapBRef={mapBRef}
              releaseA={releaseA}
              releaseB={releaseB}
              location={selectedLocation?.name || 'Unknown Location'}
            />
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default CompareSwipe;
